import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { deleteHeldFile, touchHeldFile } from '../services/attachments';
import { forgetAttachmentVariants } from '../utils/attachmentVariants';
import { normalizeId } from '../utils/ids';

/**
 * The item form's HELD FILES (docs/plans/data-types-file-element.md §3,
 * §15.1, §16.1 H1; the 10b contract). A file element uploads the moment a
 * file is picked, before the item exists; the save claims the upload. Until
 * then the form owns it, and this hook keeps the books:
 *
 *   - `register` — an upload this form made (owned: the form deletes it if
 *     it is never saved);
 *   - `seed` — files the form was handed: an applied draft's held files, or
 *     a Duplicate's source files (never owned: the draft or the source item
 *     keeps them, and the form never deletes them);
 *   - `lookup(id)` — the facts and where the thumbnail comes from:
 *     `{ heldIn: collectionId }` (the held route) or `{ itemId }` (a source
 *     item's attachment);
 *   - `settle(keepIds)` — the form is done: every owned upload not in
 *     `keepIds` is deleted (best effort, never blocking), then forgotten.
 *     A save keeps what it sent (the server claimed it); a draft kept on
 *     leaving keeps what the form holds; Cancel, Discard and leaving an edit
 *     form keep nothing. A failed save settles nothing, so the retry has
 *     them.
 *
 * `alive()` says whether the form is still open. A file input whose entry was
 * folded away (a repeating group's entries fold) unmounts while its upload is
 * on the way; the upload is still the form's, so it is kept, and only an upload
 * that lands after the FORM is gone is nobody's to save.
 *
 * While the form stays open it touches its owned uploads every 15 minutes
 * (the server keeps one an hour after its last touch, H1), so a long edit
 * never loses a file under it. A touch never shortens a draft's hold.
 */

export const HELD_FILE_TOUCH_MS = 15 * 60 * 1000;

export const HeldFilesContext = createContext(null);

/** The form's held files, or null outside an item form. */
export const useOptionalHeldFiles = () => useContext(HeldFilesContext);

export default function useHeldFiles({ collectionId }) {
	const collectionRef = useRef(normalizeId(collectionId));
	collectionRef.current = normalizeId(collectionId);
	// id -> { facts, source, element, owned }
	const entriesRef = useRef(new Map());
	const [version, setVersion] = useState(0);
	const bump = useCallback(() => setVersion(v => v + 1), []);
	const aliveRef = useRef(true);
	useEffect(() => {
		aliveRef.current = true;
		return () => { aliveRef.current = false; };
	}, []);
	const alive = useCallback(() => aliveRef.current, []);

	const register = useCallback((attachment, elementName) => {
		const id = normalizeId(attachment?._id);
		if (!id) return;
		entriesRef.current.set(id, {
			facts: { ...attachment, _id: id },
			source: { heldIn: normalizeId(attachment?.collection_id) || collectionRef.current },
			element: elementName || null,
			owned: true,
		});
		bump();
	}, [bump]);

	/**
	 * `files` is an item read's or a draft's facts map (`{ id: { name, ext,
	 * mime, size, image, variants } }`); `source` where their thumbnails come
	 * from. A facts entry naming its own item (`itemId`) reads from that item.
	 */
	const seed = useCallback((files, source) => {
		if (!files || typeof files !== 'object') return;
		let changed = false;
		Object.entries(files).forEach(([rawId, facts]) => {
			const id = normalizeId(rawId);
			if (!id || !facts || typeof facts !== 'object' || entriesRef.current.get(id)?.owned) return;
			const ownItem = normalizeId(facts.itemId || facts.subjectId);
			entriesRef.current.set(id, {
				facts: {
					_id: id,
					originalName: typeof facts.name === 'string' ? facts.name : (facts.originalName || ''),
					ext: facts.ext || '',
					mime: facts.mime || '',
					size: Number.isFinite(facts.size) ? facts.size : null,
					image: facts.image && typeof facts.image === 'object' ? facts.image : null,
					variants: Array.isArray(facts.variants) ? facts.variants : [],
				},
				source: ownItem ? { itemId: ownItem } : (source || { heldIn: collectionRef.current }),
				element: null,
				owned: false,
			});
			changed = true;
		});
		if (changed) bump();
	}, [bump]);

	const lookup = useCallback(id => entriesRef.current.get(normalizeId(id)) || null, []);

	const uploadsFor = useCallback(elementName => [...entriesRef.current.entries()]
		.filter(([, entry]) => entry.owned && entry.element === elementName)
		.map(([id]) => id), []);

	const ownedIds = useCallback(() => [...entriesRef.current.entries()]
		.filter(([, entry]) => entry.owned)
		.map(([id]) => id), []);

	const settle = useCallback((keepIds = []) => {
		const keep = new Set((keepIds || []).map(normalizeId));
		let changed = false;
		[...entriesRef.current.entries()].forEach(([id, entry]) => {
			if (!entry.owned) return;
			entriesRef.current.delete(id);
			changed = true;
			if (keep.has(id)) return;
			const where = entry.source?.heldIn || collectionRef.current;
			forgetAttachmentVariants(id);
			if (where) deleteHeldFile(where, id).catch(() => { /* the server reaps it within the hour */ });
		});
		if (changed) bump();
	}, [bump]);

	useEffect(() => {
		const timer = setInterval(() => {
			entriesRef.current.forEach((entry, id) => {
				if (!entry.owned) return;
				const where = entry.source?.heldIn || collectionRef.current;
				if (where) touchHeldFile(where, id).catch(() => { /* a later touch, or the save, decides */ });
			});
		}, HELD_FILE_TOUCH_MS);
		return () => clearInterval(timer);
	}, []);

	const resolvedCollectionId = normalizeId(collectionId);
	const value = useMemo(() => ({
		collectionId: resolvedCollectionId,
		register,
		seed,
		lookup,
		uploadsFor,
		alive,
		version,
	}), [resolvedCollectionId, register, seed, lookup, uploadsFor, alive, version]);

	return { value, settle, seed, ownedIds };
}
